import React, { useState, useEffect } from 'react'
import api from '../../utils/apiClient'
import { jwtDecode } from 'jwt-decode'
import { useLocation } from 'react-router-dom'

import ConnectEmailAccountModal from './ConnectEmailAccountModal'
import EmailAccountList from './EmailAccountList'
import { callExtension } from '../../utils/extensionBridge'
import useGlobalTheme from '../../hooks/useGlobalTheme'

const notifyEmailAccountsChanged = () =>
	callExtension('LOADLINE_EMAIL_ACCOUNTS_CHANGED', { timestamp: Date.now() })
		.catch(err => {
			if (!err?.isNotInstalled && !err?.isUnsupported) {
				console.error('Could not notify extension:', err)
			}
		})

function EmailSettings() {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const [accounts, setAccounts] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [isConnectModalOpen, setIsConnectModalOpen] = useState(false)
	const [isMaxAccountsModalOpen, setIsMaxAccountsModalOpen] = useState(false)
	const location = useLocation()

	const jwtToken = localStorage.getItem('jwtToken')
	const user = jwtToken ? jwtDecode(jwtToken) : null

	const fetchEmailData = async () => {
		try {
			const accountsResponse = await api.get('/api/email/accounts')
			setAccounts(accountsResponse.data.accounts)
			setLoading(false)
		} catch (err) {
			setError('Error fetching email data')
			setLoading(false)
		}
	}

	useEffect(() => {
		fetchEmailData()
	}, [])

	useEffect(() => {
		const urlParams = new URLSearchParams(location.search)
		const emailSetup = urlParams.get('emailSetup')

		if (emailSetup === 'success') {
			window.history.replaceState({}, document.title, location.pathname)
			notifyEmailAccountsChanged()
		}
	}, [location])

	const handleEmailAuthorization = async (provider, smtpConfig = null) => {
		try {
			if (accounts.length >= 3) {
				setIsMaxAccountsModalOpen(true)
				return
			}

			if (provider === 'smtp') {
				const response = await api.post(
					'/api/user/connect-smtp',
					smtpConfig
				)
				if (response.data.success) {
					fetchEmailData()
					setIsConnectModalOpen(false)
					setError(null)
				}
			} else {
				const response = await api.post(
					'/api/user/request-email-access',
					{
						provider,
						includeReadAccess: smtpConfig?.includeReadAccess,
					}
				)

				if (response.data.authUrl) {
					window.location.href = response.data.authUrl
				}
			}
		} catch (error) {
			console.error('Error requesting email access:', error)
			if (error.response && error.response.status === 400) {
				setError(error.response.data.message)
			} else {
				setError('An error occurred while requesting email access')
			}
		}
	}

	const handleDeleteAccount = async accountId => {
		try {
			await api.delete(`/api/user/email-account/${accountId}`)
			fetchEmailData()
			notifyEmailAccountsChanged()
		} catch (error) {
			console.error('Error deleting email account:', error)
			throw new Error(
				error.response?.data?.message || 'Failed to revoke access'
			)
		}
	}

	const handleReAuthorization = async (provider = 'gmail', email = null) => {
		try {
			const response = await api.post(
				'/api/user/re-authorize-email',
				{ provider, email }
			)

			if (response.data.authUrl) {
				window.location.href = response.data.authUrl
			}
		} catch (error) {
			console.error('Error requesting email access:', error)
			if (error.response && error.response.status === 400) {
				setError(error.response.data.message)
			} else {
				setError('An error occurred while requesting email access')
			}
		}
	}

	const handleModalOpen = () => {
		if (accounts.length >= 3) {
			setIsMaxAccountsModalOpen(true)
			return
		}

		setIsConnectModalOpen(true)
	}

	if (loading) return (
		<div className={isLight ? 'text-gray-900' : 'text-white'}>
			<section className="mb-8">
				<div className="flex justify-between items-center mb-2">
					<h3 className="text-xl font-semibold">Email Accounts</h3>
					<button
						className={`bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded text-sm ${isLight ? 'text-white' : ''}`}
						disabled
					>
						+ Connect New Account
					</button>
				</div>
				<p className={`text-sm mb-4 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>Manage your connected email accounts and permissions</p>
				<div className="space-y-3">
					{[1, 2, 3].map((index) => (
						<div key={index} className={`p-4 rounded-lg border ${isLight ? 'bg-gray-50 border-gray-200' : 'bg-[#263340] border-[#374151]'}`}>
							<div className="animate-pulse">
								<div className="flex items-center justify-between mb-3">
									<div className="flex items-center space-x-3">
										<div className={`w-10 h-10 rounded-full ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`}></div>
										<div className="space-y-2">
											<div className={`h-4 rounded w-32 ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`}></div>
											<div className={`h-3 rounded w-24 ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`}></div>
										</div>
									</div>
									<div className="flex space-x-2">
										<div className={`h-8 rounded w-20 ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`}></div>
										<div className={`h-8 rounded w-24 ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`}></div>
									</div>
								</div>
								<div className="flex items-center space-x-4">
									<div className={`h-3 rounded w-28 ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`}></div>
									<div className={`h-3 rounded w-28 ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`}></div>
								</div>
							</div>
						</div>
					))}
				</div>
			</section>
		</div>
	)

	return (
		<>
			<div className={isLight ? 'text-gray-900' : 'text-white'}>
				<section className="mb-8">
					{error && (
						<div className={`px-4 py-3 rounded-lg mb-4 flex items-center justify-between ${
							isLight
								? 'bg-red-500/10 border border-red-500/50 text-red-600'
								: 'bg-red-500/10 border border-red-500/50 text-red-400'
						}`}>
							<span>{error}</span>
							<button
								onClick={() => setError(null)}
								className={isLight ? 'text-red-600 hover:text-red-700' : 'text-red-400 hover:text-red-300'}
							>
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>
					)}
					<div className="flex justify-between items-center mb-2">
						<h3 className="text-xl font-semibold">Email Accounts</h3>
						<button
							className={`bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded text-sm ${isLight ? 'text-white' : ''}`}
							onClick={handleModalOpen}
						>
							+ Connect New Account
						</button>
					</div>
					<p className={`text-sm mb-4 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>Manage your connected email accounts and permissions</p>
					{accounts.length === 0 ? (
						<div className={`p-4 rounded-lg border text-center ${
							isLight
								? 'bg-gray-50 border-gray-200'
								: 'bg-[#263340] border-[#374151]'
						}`}>
							<p className={isLight ? 'text-gray-500' : 'text-gray-400'}>No email accounts connected yet.</p>
						</div>
					) : (
						<EmailAccountList
							accounts={accounts}
							user={user}
							onReAuthorization={handleReAuthorization}
							onDeleteAccount={handleDeleteAccount}
						/>
					)}
				</section>
			</div>

			<ConnectEmailAccountModal
				isOpen={isConnectModalOpen}
				onClose={() => setIsConnectModalOpen(false)}
				onAuthorize={handleEmailAuthorization}
			/>

			{isMaxAccountsModalOpen && (
				<>
					<div
						className="fixed inset-0 bg-black transition-opacity duration-300 opacity-50 z-40"
						onClick={() => setIsMaxAccountsModalOpen(false)}
					></div>
					<div className="fixed inset-0 flex items-center justify-center -mt-32 z-50">
						<div className={`rounded-lg p-8 w-[32rem] h-[15rem] relative ${
							isLight
								? 'bg-white ring-1 ring-gray-200 shadow-lg'
								: 'bg-[#15202b] ring-1 ring-white/10 shadow-[0_0_15px_rgba(59,130,246,0.1)]'
						}`}>
							<div className="flex justify-between items-start">
								<h2 className={`text-2xl font-bold ${isLight ? 'text-gray-900' : 'text-white'}`}>
									Maximum Accounts Reached
								</h2>
								<button
									onClick={() => setIsMaxAccountsModalOpen(false)}
									className={`-mt-1 ${isLight ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-300'}`}
								>
									<svg
										className="w-4 h-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M6 18L18 6M6 6l12 12"
										></path>
									</svg>
								</button>
							</div>
							<p className={`mt-6 mb-6 text-lg ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
								You have reached the maximum limit of 3 email accounts. Please
								delete an existing account before adding a new one.
							</p>
							<div className="absolute bottom-8 right-8">
								<button
									onClick={() => setIsMaxAccountsModalOpen(false)}
									className={`px-5 py-2.5 rounded-lg transition-colors duration-150 ease-in-out text-sm font-semibold ${
										isLight
											? 'bg-gray-100 text-gray-900 hover:bg-gray-200'
											: 'bg-[#263340] text-white hover:bg-[#2c3b4a]'
									}`}
								>
									Close
								</button>
							</div>
						</div>
					</div>
				</>
			)}
		</>
	)
}

export default EmailSettings
